import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../../shared/workspace-element-context';
import { RdSalesAdjPercentageEditService } from './rd-sales-adj-percentage-edit.service';

@Component({
    selector: 'gtw-rd-sales-adj-percentage-edit',
    templateUrl: './rd-sales-adj-percentage-edit.component.html',
    styleUrls: ['./rd-sales-adj-percentage-edit.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RdSalesAdjPercentageEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal title - dynamically set based on mode
  modalTitle = "";

  // Form data
  taxYear: any;
  percentage: string = '';
  isCreate = false; // Will be determined automatically based on rowData
  isSaveDisabled = false;

  // Data from AngularJS
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  gridData: any[] = [];
  selectedTaxYear: any = null; // For ADD mode tax year

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private rdSalesAdjPercentageEditService: RdSalesAdjPercentageEditService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  init() {
    try {
      // Automatically determine mode based on rowData content
      this.determineMode();
      
      if (this.isCreate) {
        // ADD mode - use data from GlobalService
        this.initializeAddMode();
      } else {
        // EDIT mode - use data from rowData
        this.initializeEditMode();
        this.checkLockStatus();
      }
    } catch (e) {
      console.error('Error in rd-sales-adj-percentage init:', e);
    }
  }


  private determineMode() {
    // Logic: if rowData is empty or doesn't have required fields = ADD mode
    // Same logic as AngularJS: vm.isCreate = (gridData.length == 0) ? true : false;
    this.isCreate = !this.rowData || 
                   Object.keys(this.rowData).length === 0 || 
                   !this.rowData.TAX_YEAR || 
                   !this.rowData.ADJ_KEY;
    
    // Set modal title based on mode
    this.modalTitle = this.isCreate ? "Add Percentage" : "Edit Percentage";
  }

  private initializeAddMode() {
    // ADD mode - use selectedTaxYear from AngularJS controller
    this.taxYear = this.selectedTaxYear?.value || this.filterParams?.tax_year || this.globalService?.tax_year;
    this.percentage = ''; // Empty for new records
  }

  private initializeEditMode() {
    // EDIT mode - use existing data from rowData
    this.taxYear = this.rowData.TAX_YEAR;
    this.percentage = this.rowData.PERCENTAGE || '';
  }

  private checkLockStatus() {
    // Only check lock status in EDIT mode
    if (this.isCreate) {
      return; // Skip lock check for ADD mode
    }

    const lockParams = {
      action_code: 'n10cpv',
      p_scenario: this.globalService?.scenario || this.filterParams?.scenario,
      p_jcd_key: this.globalService?.jcd_key || this.filterParams?.jcd_key
    };

    this.rdSalesAdjPercentageEditService.checkLockStatus(
      this.GENERAL_CONFIG.base_url,
      lockParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response?.jsonObject?.[0]?.LOCK_STATUS === 'Y') {
          this._snackBar.open('Tax Return has already been filed for tax year ' + this.taxYear + '.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cancel();
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  save() {
    // Validate current year constraint
    if ((this.getCurrentYear() - 1) < this.taxYear) {
      this._snackBar.open('For ' + this.taxYear + ' tax year percentage can not be created', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    // Validate percentage is provided
    if (!this.percentage || this.percentage === undefined || this.percentage === '') {
      this._snackBar.open('Percentage is required', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build settings object
    const clobSettingsObj = {
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      scenario: this.globalService?.scenario || this.filterParams?.scenario,
      jcd_key: this.globalService?.jcd_key || this.filterParams?.jcd_key
    };

    // Build data object - different for ADD vs EDIT mode
    const returnObj = {
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.taxYear,
      scenario: this.globalService?.scenario || this.filterParams?.scenario,
      jcd_key: this.globalService?.jcd_key || this.filterParams?.jcd_key,
      operation_type: this.isCreate ? 'I' : 'U', // Insert for ADD, Update for EDIT
      code_key: this.isCreate ? null : this.rowData.ADJ_KEY, // null for ADD, existing key for EDIT
      percentage: this.percentage
    };

    const clobDataDetails = [returnObj];

    this.rdSalesAdjPercentageEditService.savePercentage(
      this.GENERAL_CONFIG.base_url,
      clobSettingsObj,
      clobDataDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          const message = this.isCreate 
            ? 'R&d Percentage has been successfully added'
            : 'R&d Percentage has been successfully updated';
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        console.error('Save error:', error);
      }
    );
  }

  reset() {
    if (this.isCreate) {
      // ADD mode - clear the percentage field
      this.percentage = '';
    } else {
      // EDIT mode - reset to original value from rowData
      this.percentage = this.rowData.PERCENTAGE || '';
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Input validation for percentage field
  onPercentageInput(event: any) {
    let value = event.target.value;
    
    // Allow only numbers and one decimal point
    value = value.replace(/[^0-9.]/g, '');
    
    // Allow only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    // Ensure value is between 0 and 100
    const numValue = parseFloat(value);
    if (numValue < 0) {
      value = '0';
    } else if (numValue > 100) {
      value = '100';
    }
    
    this.percentage = value;
    event.target.value = value;
  }

  // Directive logic for percentage input (limitToMaxFromOne)
  onKeyPress(event: KeyboardEvent) {
    const charCode = event.charCode;
    if (!(charCode >= 46 && charCode <= 57) || charCode === 47) {
      event.preventDefault();
    }
    const inputElement = event.target as HTMLInputElement;
    const newVal = inputElement.value + (charCode !== 0 ? String.fromCharCode(charCode) : '');
    if (String.fromCharCode(charCode) === ".") {
      if (inputElement.value.indexOf(".") !== -1) {
        event.preventDefault();
      }
    }
    if (inputElement.value.search(/(.*)\.[0-9][0-9]/) === 0 && newVal.length > inputElement.value.length) {
      event.preventDefault();
    }
  }

  onKeyUp(event: KeyboardEvent) {
    const inputElement = event.target as HTMLInputElement;
    let decimalData = inputElement.value;
    if (parseFloat(decimalData) < 0 || parseFloat(decimalData) > 100) {
      inputElement.value = '100';
      this.percentage = '100';
    }

    function countDecimalPlaces() {
      const decimalPos = String(decimalData).indexOf('.');
      if (decimalPos === -1) {
        return 0;
      } else {
        return String(decimalData).length - decimalPos - 1;
      }
    }

    if (countDecimalPlaces() > 2) {
      inputElement.value = Number(decimalData).toFixed(2);
      this.percentage = Number(decimalData).toFixed(2);
    }
  }
}
